import { useState, useEffect, useCallback } from "react";
import { dashboardService, DashboardItem } from "../services/dashboardService";

const MIGRATION_KEY_PREFIX = "urls_migrated_";

function hasRunUrlMigration(userId: string): boolean {
  try {
    return localStorage.getItem(`${MIGRATION_KEY_PREFIX}${userId}`) === "1";
  } catch {
    return false;
  }
}

function markUrlMigrationRun(userId: string): void {
  try {
    localStorage.setItem(`${MIGRATION_KEY_PREFIX}${userId}`, "1");
  } catch {
    // Migration is idempotent, so re-running it is harmless.
  }
}

export function useDashboard(userId: string | undefined) {
  const [dashboardItems, setDashboardItems] = useState<DashboardItem[]>([]);
  const [loading, setLoading] = useState(!!userId);
  /** A failed write (add, edit, reorder). The UI shows it as a toast. */
  const [error, setError] = useState<Error | null>(null);
  /** A failed subscription — the dashboard could not be read at all. */
  const [loadError, setLoadError] = useState<Error | null>(null);

  // A different user (or none) starts from nothing, so one account's links
  // never show under another's while the new snapshot loads. Reset while
  // rendering rather than in the effect, so no frame shows the stale list.
  const [subscribedUserId, setSubscribedUserId] = useState(userId);
  if (userId !== subscribedUserId) {
    setSubscribedUserId(userId);
    setDashboardItems([]);
    setLoading(!!userId);
    setLoadError(null);
  }

  useEffect(() => {
    if (!userId) return;

    // The SDK's persistent cache delivers the first snapshot from IndexedDB
    // on a returning device, so there is no separate localStorage copy to
    // read here any more; the skeleton shows only until that first snapshot.

    // Subscribe first. The legacy URL migration used to gate this in a
    // .finally(), which put two extra sequential round trips (a getDoc plus a
    // getDocs over users/{uid}/urls) in front of every single dashboard load —
    // forever, including for users who migrated years ago.
    const unsubscribe = dashboardService.subscribeToDashboard(
      userId,
      (items: DashboardItem[]) => {
        setDashboardItems(items || []);
        setLoadError(null);
        setLoading(false);
      },
      (err) => {
        // Leaving `loading` true here would show the skeleton forever; the
        // dashboard renders the error instead.
        setLoadError(err);
        setLoading(false);
      }
    );

    // Migration runs in the background and writes through the same document, so
    // the subscription above picks up its result. Gated to once per user per
    // device; the service is still idempotent if the flag is lost.
    if (!hasRunUrlMigration(userId)) {
      dashboardService
        .migrateUrlsToDashboard(userId)
        .then(() => markUrlMigrationRun(userId))
        .catch((err) => {
          console.error("Error migrating URLs:", err);
        });
    }

    return () => {
      unsubscribe();
    };
  }, [userId]);

  const clearError = useCallback(() => setError(null), []);

  // Add a new item to the dashboard
  const addDashboardItem = async (item: Omit<DashboardItem, "id">) => {
    if (!userId) throw new Error("User not authenticated");
    setError(null);
    try {
      const newId = await dashboardService.addDashboardItem(userId, item);
      return newId;
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to add dashboard item");
      setError(error);
      throw error;
    }
  };

  // Update an existing dashboard item
  const updateDashboardItem = async (itemId: string, updates: Partial<DashboardItem>) => {
    if (!userId) throw new Error("User not authenticated");
    setError(null);
    try {
      await dashboardService.updateDashboardItem(userId, itemId, updates);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to update dashboard item");
      setError(error);
      throw error;
    }
  };

  // Delete a dashboard item
  const deleteDashboardItem = async (itemId: string) => {
    if (!userId) throw new Error("User not authenticated");
    setError(null);
    try {
      await dashboardService.deleteDashboardItem(userId, itemId);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to delete dashboard item");
      setError(error);
      throw error;
    }
  };

  // Update the position of a dashboard item
  const updateItemPosition = async (itemId: string, col: number, order: number) => {
    if (!userId) throw new Error("User not authenticated");
    setError(null);
    try {
      await dashboardService.updateItemPosition(userId, itemId, col, order);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to update item position");
      setError(error);
      throw error;
    }
  };

  // Move items to new positions (drag-and-drop, column changes). Merged by id
  // on the server; the snapshot listener delivers the result, with the SDK's
  // latency compensation making it appear immediately.
  const updateDashboardLayout = async (items: DashboardItem[]) => {
    if (!userId) throw new Error("User not authenticated");
    setError(null);
    try {
      await dashboardService.applyLayout(userId, items);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to update dashboard layout");
      setError(error);
      throw error;
    }
  };

  return {
    dashboardItems,
    loading,
    error,
    loadError,
    clearError,
    addDashboardItem,
    updateDashboardItem,
    deleteDashboardItem,
    updateItemPosition,
    updateDashboardLayout,
  };
}
